/*
  Lander kit behaviour for sections/lander-*.liquid. Loaded with `defer` by
  layout/lander.liquid and layout/lander-with-footer.liquid.

  Urgency bar ([data-lander-ends-at]): the end time comes from the section's
  own setting (a real deadline, never a per-visitor timer). The server already
  omits the bar after the deadline; this also hides it in the browser for
  pages served from cache, and runs the optional countdown.
*/
(function () {
  'use strict';

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function formatRemaining(ms) {
    var total = Math.floor(ms / 1000);
    var days = Math.floor(total / 86400);
    var hours = Math.floor((total % 86400) / 3600);
    var minutes = Math.floor((total % 3600) / 60);
    var seconds = total % 60;
    var hms = pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
    return days > 0 ? days + 'd ' + hms : hms;
  }

  function initUrgencyBar(bar) {
    var endsAt = parseInt(bar.getAttribute('data-lander-ends-at'), 10) * 1000;
    if (!endsAt) return;
    var countdown = bar.querySelector('[data-lander-countdown]');

    function tick() {
      var remaining = endsAt - Date.now();
      if (remaining <= 0) {
        bar.hidden = true;
        return false;
      }
      if (countdown) countdown.textContent = formatRemaining(remaining);
      return true;
    }

    if (tick() && countdown) {
      var timer = setInterval(function () {
        if (!tick()) clearInterval(timer);
      }, 1000);
    }
  }

  /*
    Buy box ([data-lander-buy-box], sections/lander-buy-box.liquid).
    Every value used here is rendered by Liquid from the products and their
    selling plans, so nothing about the offer is hardcoded in this file.
    Checked end to end by scripts/verify_lander_cart.mjs.
  */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(
          function (data) {
            throw new Error(data.description || data.message || 'Cart error ' + res.status);
          },
          function () {
            throw new Error('Cart error ' + res.status);
          }
        );
      }
      return res.json();
    });
  }

  // EasyGift adds the Welcome Package (and the annual bonus book) after the
  // plan lands in the cart. Wait briefly for it so checkout opens with the
  // gift already there. Give up quietly after 2 s: a missing gift must never
  // block checkout.
  function waitForGift(handle) {
    if (!handle) return Promise.resolve();
    var deadline = Date.now() + 2000;
    function poll() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var found = cart.items.some(function (i) {
            return i.handle === handle;
          });
          if (found || Date.now() > deadline) return;
          return new Promise(function (r) {
            setTimeout(r, 150);
          }).then(poll);
        })
        .catch(function () {});
    }
    return poll();
  }

  function initBuyBox(box) {
    if (box.dataset.landerReady) return;
    box.dataset.landerReady = 'true';

    var radios = Array.prototype.slice.call(box.querySelectorAll('[data-lander-plan]'));
    var buttons = Array.prototype.slice.call(box.querySelectorAll('[data-lander-atc]'));
    var errorEl = box.querySelector('[data-lander-error]');
    var sticky = box.querySelector('[data-lander-sticky]');
    var stickyPrice = box.querySelector('[data-lander-sticky-price]');
    if (!radios.length || !buttons.length) return;

    function selected() {
      return (
        radios.filter(function (r) {
          return r.checked;
        })[0] || radios[0]
      );
    }

    function render() {
      var current = selected();
      // v2's look: the selected plan's container gets the bold border
      // (freedom-island.css .fibc-product__info-open).
      radios.forEach(function (r) {
        var container = r.closest('.fibc-product__container');
        if (container) container.classList.toggle('fibc-product__info-open', r === current);
      });
      box.querySelectorAll('[data-lander-renew-for]').forEach(function (p) {
        p.hidden = p.getAttribute('data-lander-renew-for') !== current.value;
      });
      if (stickyPrice) stickyPrice.textContent = current.dataset.price + '/month';
    }

    function setBusy(busy) {
      buttons.forEach(function (b) {
        if (busy) {
          b.dataset.label = b.textContent;
          b.textContent = 'Adding…';
        } else if (b.dataset.label) {
          b.textContent = b.dataset.label;
        }
        b.disabled = busy;
      });
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }

    function add() {
      var plan = selected();
      var items = [
        {
          id: Number(plan.dataset.variantId),
          quantity: 1,
          selling_plan: Number(plan.dataset.sellingPlan),
        },
      ];
      if (plan.dataset.freeVariant) {
        items.push({
          id: Number(plan.dataset.freeVariant),
          quantity: 1,
          properties: { _free_book_vid: plan.dataset.freeSku },
        });
      }
      var toCheckout = box.dataset.to === 'checkout';
      showError('');
      setBusy(true);

      var ready = toCheckout ? postJSON('/cart/clear.js') : Promise.resolve();
      ready
        .then(function () {
          return postJSON('/cart/add.js', { items: items });
        })
        .then(function () {
          return toCheckout ? waitForGift(box.dataset.giftWait) : null;
        })
        .then(function () {
          var target = toCheckout ? '/checkout' : '/cart';
          var code = box.dataset.discount;
          window.location.href = code ? '/discount/' + code + '?redirect=' + encodeURIComponent(target) : target;
        })
        .catch(function (err) {
          setBusy(false);
          showError('Sorry, that didn’t work: ' + err.message + '. Please try again.');
        });
    }

    radios.forEach(function (r) {
      r.addEventListener('change', render);
    });
    buttons.forEach(function (b) {
      b.addEventListener('click', add);
    });
    render();

    // Sticky bar: visible once the hero has scrolled away, hidden while the
    // buy box itself is on screen.
    var hero = document.querySelector('[data-lander-hero]');
    if (sticky && hero && 'IntersectionObserver' in window) {
      var heroVisible = true;
      var boxVisible = false;
      var update = function () {
        sticky.hidden = heroVisible || boxVisible;
      };
      new IntersectionObserver(function (entries) {
        heroVisible = entries[0].isIntersecting;
        update();
      }).observe(hero);
      new IntersectionObserver(function (entries) {
        boxVisible = entries[0].isIntersecting;
        update();
      }).observe(box.querySelector('.lander-buy__panel'));
    }
  }

  function init(root) {
    root.querySelectorAll('[data-lander-ends-at]').forEach(initUrgencyBar);
    root.querySelectorAll('[data-lander-buy-box]').forEach(initBuyBox);
  }

  init(document);

  // Theme editor: re-run when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
